<script lang="ts">
	import { Confetti } from 'svelte-confetti';
	import { toast } from 'svelte-sonner';
	import { getContext, onMount, onDestroy, tick } from 'svelte';

	import { exportChatStats, exportSingleChatStats, downloadChatStats } from '$lib/apis/chats';
	import { getVersion } from '$lib/apis';
	import { settings } from '$lib/stores';
	import { COMMUNITY_ORIGINS } from '$lib/constants';

	import Modal from '$lib/components/common/Modal.svelte';
	import Tooltip from '$lib/components/common/Tooltip.svelte';
	import XMark from '$lib/components/icons/XMark.svelte';
	import Spinner from '$lib/components/common/Spinner.svelte';

	const i18n = getContext('i18n');

	export let show = false;
	export let eventData = null;

	// Listen for verify:chat messages from opener
	const handleMessage = async (event: MessageEvent) => {
		if (!COMMUNITY_ORIGINS.includes(event.origin)) {
			return;
		}

		// Community sends: { type: 'verify:chat', data: { id: ... } }
		const chatId = event.data?.data?.id ?? event.data?.id;
		if (event.data?.type === 'verify:chat' && chatId) {
			try {
				const res = await exportSingleChatStats(localStorage.token, chatId);
				if (res && window.opener) {
					window.opener.postMessage(
						{
							type: 'verify:chat:response',
							data: res,
							chatId: chatId,
							requestId: event.data.requestId ?? null
						},
						event.origin
					);
				}
			} catch (err: any) {
				console.error('Failed to verify chat:', err);
				if (window.opener) {
					window.opener.postMessage(
						{
							type: 'verify:chat:error',
							error: err?.detail || err?.message || 'Failed to verify chat',
							chatId: chatId,
							requestId: event.data.requestId ?? null
						},
						event.origin
					);
				}
			}
		}
	};

	// Watch for modal visibility changes to notify opener
	$: if (show && window.opener) {
		window.opener.postMessage('loaded', '*');
	}

	onMount(() => {
		window.addEventListener('message', handleMessage);
	});

	onDestroy(() => {
		window.removeEventListener('message', handleMessage);
	});

	// State
	let syncing = false;
	let downloading = false;
	let completed = false;
	let error = false;
	let errorMessage = '';

	// Progress tracking
	let processedItemsCount = 0;
	let total = 0;

	// Download controller for cancellation
	let downloadController: AbortController | null = null;

	// Sync mode: 'incremental' or 'full'
	let syncMode: 'incremental' | 'full' = 'incremental';

	// Reactive progress percentage
	$: progressPercent =
		total > 0 ? Math.min(Math.round((processedItemsCount / total) * 100), 100) : 0;

	// Helper to send postMessage to opener
	const postToOpener = (message: object) => {
		if (window.opener) {
			const payload = { ...message, requestId: eventData?.requestId ?? null };
			for (const origin of COMMUNITY_ORIGINS) {
				window.opener.postMessage(payload, origin);
			}
		}
	};

	// Reset all state
	const resetState = () => {
		syncing = false;
		downloading = false;
		completed = false;
		error = false;
		errorMessage = '';
		processedItemsCount = 0;
		total = 0;
		downloadController = null;
	};

	// Handle error state
	const handleError = (message: string) => {
		console.error('Sync error:', message);
		errorMessage = message;
		error = true;
		syncing = false;
		downloading = false;
		postToOpener({ type: 'sync:error', error: message });
	};

	// Cancel ongoing operations
	const cancelOperation = () => {
		if (downloadController) {
			downloadController.abort();
			downloadController = null;
		}
		syncing = false;
		downloading = false;

		postToOpener({ type: 'sync:error', error: 'User cancelled the operation' });
	};

	// Sync stats to opener window
	const syncStats = async () => {
		if (window.opener) {
			window.opener.focus();
		}
		postToOpener({ type: 'sync:start' });

		syncing = true;
		error = false;
		errorMessage = '';
		processedItemsCount = 0;
		total = 0;

		try {
			// Get version info
			const versionRes = await getVersion(localStorage.token).catch((err) => {
				console.error('Failed to get version:', err);
				return null;
			});

			if (versionRes) {
				postToOpener({ type: 'sync:version', data: versionRes });
			}

			// Paginate through stats
			let page = 1;
			let allItemsLoaded = false;

			while (!allItemsLoaded) {
				// Build search params, include updated_at for incremental mode
				const searchParams = { ...(eventData?.searchParams ?? {}) };
				if (syncMode === 'incremental' && eventData?.lastSyncedChatUpdatedAt) {
					searchParams.updated_at = eventData.lastSyncedChatUpdatedAt;
				}

				const res = await exportChatStats(localStorage.token, page, searchParams).catch((err) => {
					throw new Error(err?.detail || err?.message || 'Failed to export chat stats');
				});

				if (!res) {
					throw new Error('Failed to fetch stats data');
				}

				processedItemsCount += res.items.length;
				total = res.total;

				// Allow UI to repaint
				await tick();

				if (window.opener && res.items.length > 0) {
					postToOpener({ type: 'sync:stats:chats', data: res });
				}

				if (processedItemsCount >= total || res.items.length === 0) {
					allItemsLoaded = true;
				} else {
					page += 1;
				}
			}

			// Success
			postToOpener({ type: 'sync:complete' });
			syncing = false;
			completed = true;
		} catch (err: any) {
			handleError(err?.message || 'An unexpected error occurred');
		}
	};

	// Download stats as JSON file
	const downloadHandler = async () => {
		if (downloading) {
			cancelOperation();
			return;
		}

		downloading = true;
		syncing = true;
		error = false;
		errorMessage = '';
		processedItemsCount = 0;
		total = 0;

		try {
			// Get total count first (no filters for download - get all)
			const initialRes = await exportChatStats(localStorage.token, 1, {}).catch(() => null);

			if (initialRes?.total) {
				total = initialRes.total;
			}

			// Allow UI to show the total
			await tick();

			// Get version for filename
			const versionRes = await getVersion(localStorage.token).catch(() => null);
			const version = versionRes?.version ?? '0.0.0';
			const filename = `open-webui-stats-${version}-${Date.now()}.json`;

			// Start streaming download
			const searchParams = eventData?.searchParams ?? {};
			const [res, controller] = await downloadChatStats(
				localStorage.token,
				searchParams.updated_at
			).catch((err) => {
				throw new Error(
					err?.detail || 'Failed to connect to the server. Please check your connection.'
				);
			});

			if (!res) {
				throw new Error('Failed to start download. The server may be unavailable.');
			}

			downloadController = controller;
			const reader = res.body.getReader();
			const decoder = new TextDecoder();

			const items: any[] = [];
			let buffer = '';

			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				const lines = buffer.split('\n');
				buffer = lines.pop() || '';

				for (const line of lines) {
					if (line.trim() !== '') {
						try {
							items.push(JSON.parse(line));
							processedItemsCount += 1;
						} catch (e) {
							console.error('Error parsing line:', e);
						}
					}
				}

				// Allow UI to repaint after each chunk
				await tick();
			}

			// Process remaining buffer
			if (buffer.trim() !== '') {
				try {
					items.push(JSON.parse(buffer));
					processedItemsCount += 1;
				} catch (e) {
					console.error('Error parsing buffer:', e);
				}
			}

			// Trigger download if not cancelled
			if (downloading) {
				const blob = new Blob([JSON.stringify(items)], { type: 'application/json' });
				const url = window.URL.createObjectURL(blob);
				const a = document.createElement('a');
				a.href = url;
				a.download = filename;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				window.URL.revokeObjectURL(url);
			}
		} catch (err: any) {
			// Don't show error if user cancelled the download
			if (err?.name === 'AbortError' || err?.message?.includes('aborted')) {
				// User cancelled - just reset state silently
			} else {
				handleError(err?.message || 'Download failed. Please try again.');
				toast.error(errorMessage);
			}
		} finally {
			downloading = false;
			syncing = false;
			downloadController = null;
		}
	};

	// Close modal and reset state
	const closeModal = () => {
		show = false;
		resetState();
	};
</script>

<Modal bind:show size="md" className="bg-white dark:bg-gray-900 rounded-4xl">
	<div class="w-full">
		{#if completed}
			<div class="px-5.5 py-5">
				<div class="mb-1 text-xl font-normal">{$i18n.t('Sync Complete!')}</div>
				<div class="mb-3 text-xs text-gray-500">
					{$i18n.t('Your usage stats have been successfully synced.')}
				</div>

				<Confetti x={[-0.5, 0.5]} y={[0.25, 1]} />

				<div class="flex justify-end">
					<button
						class="flex h-7 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-2.5 text-xs font-normal text-white transition hover:bg-black disabled:opacity-60 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
						on:click={closeModal}
					>
						{$i18n.t('Done')}
					</button>
				</div>
			</div>
		{:else if error}
			<div class="px-5.5 py-5">
				<div class="mb-1 text-xl font-normal">{$i18n.t('Sync Failed')}</div>
				<div class="mb-3 text-xs text-gray-500">
					{errorMessage || $i18n.t('There was an error syncing your stats. Please try again.')}
				</div>

				<div class="flex justify-end">
					<button
						class="flex h-7 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-2.5 text-xs font-normal text-white transition hover:bg-black disabled:opacity-60 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
						on:click={() => {
							error = false;
							errorMessage = '';
						}}
					>
						{$i18n.t('Try Again')}
					</button>
				</div>
			</div>
		{:else}
			<div class="flex justify-between px-4 pt-3 pb-1">
				<div class="text-sm font-medium self-center">{$i18n.t('Sync Usage Stats')}</div>
				<button
					class="self-center rounded-lg p-1 text-gray-500 transition hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200"
					aria-label={$i18n.t('Close modal')}
					on:click={() => {
						show = false;
					}}
					disabled={syncing}
				>
					<XMark className={'size-4'} />
				</button>
			</div>

			<div class="px-5 pt-2 pb-5">
				<div class="text-sm text-gray-500 dark:text-gray-400">
					<!-- LICENSE covers this Open WebUI Community wordmark.
					Do not alter, remove, obscure, or replace it except as LICENSE permits:
					https://docs.openwebui.com/license. -->
					{$i18n.t('Do you want to sync your usage stats with Open WebUI Community?')}
				</div>

				<div class="mt-2 text-xs text-gray-500">
					<!-- LICENSE covers this Open WebUI wordmark.
					Do not alter, remove, obscure, or replace it except as LICENSE permits:
					https://docs.openwebui.com/license. -->
					{$i18n.t(
						'Participate in community leaderboards and evaluations! Syncing aggregated usage stats helps drive research and improvements to Open WebUI. Your privacy is paramount: no message content is ever shared.'
					)}
				</div>

				<div class="mt-3 text-xs text-gray-500">
					<div class="mb-1 font-normal text-gray-600 dark:text-gray-400">
						{$i18n.t('What is shared:')}
					</div>
					<ul class="list-disc list-inside space-y-0.5 ml-1 mb-2">
						<!-- LICENSE covers this Open WebUI wordmark.
						Do not alter, remove, obscure, or replace it except as LICENSE permits:
						https://docs.openwebui.com/license. -->
						<li>{$i18n.t('Open WebUI version')}</li>
						<li>{$i18n.t('Model names and usage frequency')}</li>
						<li>{$i18n.t('Message counts and response timestamps')}</li>
						<li>{$i18n.t('Content lengths (character counts only)')}</li>
						<li>{$i18n.t('User ratings (thumbs up/down)')}</li>
					</ul>

					<div class="mb-1 font-normal text-gray-600 dark:text-gray-400">
						{$i18n.t('What is NOT shared:')}
					</div>
					<ul class="list-disc list-inside space-y-0.5 ml-1">
						<li>{$i18n.t('Your message text or inputs')}</li>
						<li>{$i18n.t('Model responses or outputs')}</li>
						<li>{$i18n.t('Uploaded files or images')}</li>
					</ul>
				</div>

				{#if eventData?.lastSyncedChatUpdatedAt}
					<div class="mt-3">
						<Tooltip
							content={$i18n.t(
								'Syncs only chats with updates after your last sync timestamp. Disable to re-sync all chats.'
							)}
							placement="top-start"
						>
							<label class="flex items-center gap-2 text-xs cursor-pointer">
								<input
									type="checkbox"
									checked={syncMode === 'incremental'}
									on:change={(e) => (syncMode = e.target.checked ? 'incremental' : 'full')}
									disabled={syncing}
									class="w-4 h-4 rounded border-gray-300 dark:border-gray-600"
								/>
								<span class="text-gray-700 dark:text-gray-300"
									>{$i18n.t('Only sync new/updated chats')}</span
								>
							</label>
						</Tooltip>
					</div>
				{/if}

				{#if syncing}
					<div class="mt-3 mx-1.5">
						<div class="text-xs text-gray-500 mb-1 flex justify-between">
							<div>
								{downloading ? $i18n.t('Downloading stats...') : $i18n.t('Syncing stats...')}
							</div>
							<div>
								{#if total > 0}
									{processedItemsCount}/{total}
								{/if}
							</div>
						</div>
						<div class="w-full bg-gray-200 rounded-full h-1.5 dark:bg-gray-700 overflow-hidden">
							{#if total > 0}
								<div
									class="bg-gray-900 dark:bg-gray-100 h-1.5 rounded-full transition-all duration-300"
									style="width: {progressPercent}%"
								></div>
							{:else}
								<div
									class="bg-gray-900 dark:bg-gray-100 h-1.5 w-0 rounded-full animate-pulse"
								></div>
							{/if}
						</div>
					</div>
				{/if}

				<div class="mt-4 flex items-center justify-between gap-2">
					<div class="text-xs text-gray-400 text-center mr-auto">
						<button
							class="hover:underline px-2"
							type="button"
							on:click={downloadHandler}
							disabled={syncing && !downloading}
						>
							{downloading ? $i18n.t('Stop Download') : $i18n.t('Download as JSON')}
						</button>
					</div>

					<button
						class="flex h-7 shrink-0 items-center justify-center gap-1.5 rounded-lg px-2.5 text-xs font-normal transition disabled:opacity-60 bg-gray-100 text-gray-600 hover:bg-gray-200 disabled:cursor-not-allowed dark:bg-gray-800 dark:text-gray-400 dark:hover:bg-gray-700"
						on:click={() => {
							if (syncing) {
								cancelOperation();
							} else {
								show = false;
							}
						}}
					>
						{$i18n.t('Cancel')}
					</button>

					<button
						class="flex h-7 shrink-0 items-center justify-center gap-1.5 rounded-lg bg-gray-900 px-2.5 text-xs font-normal text-white transition hover:bg-black disabled:opacity-60 dark:bg-gray-100 dark:text-gray-900 dark:hover:bg-white"
						on:click={syncStats}
						disabled={syncing}
					>
						{#if syncing && !downloading}
							<div class="flex items-center gap-2">
								<Spinner className="size-3" />
								<span>{$i18n.t('Syncing...')}</span>
							</div>
						{:else}
							{$i18n.t('Sync')}
						{/if}
					</button>
				</div>
			</div>
		{/if}
	</div>
</Modal>
